import { z } from "zod";
import { bookDetailsSchema, digitalKindSchema, fulfilmentSchema, productAuthorInputSchema, productAuthorSchema, } from "./catalog-books.js";
import { defineEndpoint, idParamsSchema } from "./endpoint.js";
import { paisa } from "./money.js";
/** Products, options, variants and barcodes (docs/specs/catalog.md, C-02 … C-07, C-10). */
export const PRODUCT_TYPES = ["standard", "service", "weighed"];
export const productTypeSchema = z.enum(PRODUCT_TYPES);
export const CATALOG_STATUSES = ["active", "archived"];
export const catalogStatusSchema = z.enum(CATALOG_STATUSES);
/**
 * A product's status (B-80b, docs/specs/catalog.md Part 4): variants stay `active`/`archived`; a product can also be
 * in the Trash (`trashed`, restorable) or `deleted` for good (a tombstone kept only for append-only price history).
 */
export const PRODUCT_STATUSES = ["active", "archived", "trashed", "deleted"];
export const productStatusSchema = z.enum(PRODUCT_STATUSES);
/** What the product list can show: never the `deleted` tombstones. */
export const PRODUCT_LIST_STATUSES = ["active", "archived", "trashed"];
export const BARCODE_KINDS = ["ean13", "upca", "code128", "internal"];
export const barcodeKindSchema = z.enum(BARCODE_KINDS);
export const MAX_OPTIONS = 3;
export const MAX_OPTION_VALUES = 50;
export const MAX_VARIANTS = 250;
export const MAX_PRODUCT_IMAGES = 8;
/** Description length in characters of (safe) HTML (ADR-0016). */
export const MAX_DESCRIPTION_LENGTH = 20_000;
const text = (max) => z.string().trim().max(max);
const optionValue = z.string().trim().min(1).max(50);
/** A barcode as scanned or typed: digits for EAN-13/UPC-A (check digit verified), printable ASCII for Code128. */
export const barcodeCodeSchema = z
    .string()
    .trim()
    .min(1)
    .max(48)
    .regex(/^[\x21-\x7E]+$/);
export const barcodeSchema = z.object({ id: z.uuid(), code: z.string(), kind: barcodeKindSchema });
export const productOptionSchema = z.object({ name: z.string(), values: z.array(z.string()) });
/** Option name → value (`{ "Size": "M", "Colour": "Red" }`); `{}` for a product without options. */
export const optionValuesSchema = z.record(z.string(), z.string());
export const variantSchema = z.object({
    id: z.uuid(),
    productId: z.uuid(),
    optionValues: optionValuesSchema,
    sku: z.string(),
    pricePaisa: paisa(),
    costPaisa: paisa(),
    mrpPaisa: paisa().nullable(),
    imageFileId: z.uuid().nullable(),
    status: catalogStatusSchema,
    barcodes: z.array(barcodeSchema),
    /** This variant's own selling price at some locations (effective price = override ?? `pricePaisa`). */
    locationPrices: z.array(z.object({ locationId: z.uuid(), pricePaisa: paisa() })),
    /** DG-05: a digital variant has no stock and is never shipped. */
    fulfilment: fulfilmentSchema.default("physical"),
    digitalKind: digitalKindSchema.nullable().default(null),
    version: z.int(),
});
export const productSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    /** Safe HTML (sanitized by the server, ADR-0016): render it as HTML with the `.rich-text` styles. */
    description: z.string(),
    type: productTypeSchema,
    categoryId: z.uuid().nullable(),
    brandId: z.uuid().nullable(),
    unitId: z.uuid(),
    /** Own VAT rate; null → the category's default → 0 (C-05). */
    taxRateBp: z.int().nullable(),
    trackInventory: z.boolean(),
    allowNegative: z.boolean(),
    status: productStatusSchema,
    imageFileIds: z.array(z.uuid()),
    tagIds: z.array(z.uuid()),
    options: z.array(productOptionSchema),
    /** Active and archived variants; archived ones keep history (no hard delete, C-10). */
    variants: z.array(variantSchema),
    /** DG-05 (BK-01): the book's details and authors (null / empty for other products). */
    book: bookDetailsSchema.nullable().default(null),
    authors: z.array(productAuthorSchema).default([]),
    version: z.int(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const productSummarySchema = z.object({
    id: z.uuid(),
    name: z.string(),
    type: productTypeSchema,
    status: productStatusSchema,
    categoryId: z.uuid().nullable(),
    brandId: z.uuid().nullable(),
    imageFileId: z.uuid().nullable(),
    activeVariantCount: z.int(),
    minPricePaisa: paisa().nullable(),
    maxPricePaisa: paisa().nullable(),
    updatedAt: z.iso.datetime(),
    /** When it went to the Trash (null unless `trashed`). */
    trashedAt: z.iso.datetime().nullable().default(null),
});
export const productListSchema = z.object({
    data: z.array(productSummarySchema),
    nextCursor: z.uuid().nullable(),
});
export const productListQuerySchema = z.object({
    categoryId: z.uuid().optional(),
    brandId: z.uuid().optional(),
    type: productTypeSchema.optional(),
    status: z.enum(PRODUCT_LIST_STATUSES).default("active"),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.uuid().optional(),
});
const optionInput = z
    .object({
    name: z.string().trim().min(1).max(30),
    values: z.array(optionValue).min(1).max(MAX_OPTION_VALUES),
})
    .strict();
/** The data for one option combination (one variant). */
export const variantInputSchema = z
    .object({
    optionValues: optionValuesSchema,
    sku: z.string().trim().min(1).max(64).optional(),
    pricePaisa: paisa().min(0),
    costPaisa: paisa().min(0).default(0),
    mrpPaisa: paisa().min(0).nullable().default(null),
    imageFileId: z.uuid().nullable().default(null),
    barcodes: z.array(barcodeCodeSchema).max(10).default([]),
    /** Generate an internal EAN-13 (`200…`) when the variant has no barcode of its own. */
    generateBarcode: z.boolean().default(false),
    /** DG-05: physical (default) or digital with its kind. */
    fulfilment: fulfilmentSchema.default("physical"),
    digitalKind: digitalKindSchema.nullable().default(null),
})
    .strict()
    .refine((v) => (v.fulfilment === "digital") === (v.digitalKind !== null), {
    message: "A digital variant needs its kind; a physical one has none",
    path: ["digitalKind"],
});
const productFields = {
    name: z.string().trim().min(1).max(200),
    /** A safe HTML subset (ADR-0016): the server sanitizes it; text without tags is turned into paragraphs. */
    description: text(MAX_DESCRIPTION_LENGTH).optional(),
    categoryId: z.uuid().nullable().optional(),
    brandId: z.uuid().nullable().optional(),
    taxRateBp: z.int().min(0).max(10_000).nullable().optional(),
    trackInventory: z.boolean().optional(),
    allowNegative: z.boolean().optional(),
    tagIds: z.array(z.uuid()).max(30).optional(),
    imageFileIds: z.array(z.uuid()).max(MAX_PRODUCT_IMAGES).optional(),
    /** DG-05 (BK-01): book details (null removes them) and the authors in order. */
    book: bookDetailsSchema.nullable().optional(),
    authors: z.array(productAuthorInputSchema).max(10).optional(),
};
/**
 * `POST /v1/products`: details, up to 3 options, and exactly one variant entry per option combination (one entry
 * with `optionValues: {}` when there are no options).
 */
export const createProductRequestSchema = z
    .object({
    ...productFields,
    type: productTypeSchema,
    unitId: z.uuid(),
    options: z.array(optionInput).max(MAX_OPTIONS).default([]),
    variants: z.array(variantInputSchema).min(1).max(MAX_VARIANTS),
})
    .strict();
/**
 * `PATCH /v1/products/{id}`: changed details and/or new options. Combinations that disappear are archived; new
 * ones need an entry in `variants`; returning ones are restored. Type and unit are fixed once chosen.
 */
export const updateProductRequestSchema = z
    .object({
    ...productFields,
    name: productFields.name.optional(),
    options: z.array(optionInput).max(MAX_OPTIONS).optional(),
    variants: z.array(variantInputSchema).max(MAX_VARIANTS).default([]),
    version: z.int().min(1),
})
    .strict();
/** `PATCH /v1/variants/{id}`: SKU and image (prices change through the prices API). */
export const updateVariantRequestSchema = z
    .object({
    sku: z.string().trim().min(1).max(64).optional(),
    imageFileId: z.uuid().nullable().optional(),
    version: z.int().min(1),
})
    .strict()
    .refine((change) => Object.keys(change).some((key) => key !== "version"), "Nothing to change");
/** `POST /v1/variants/{id}/barcodes`: a scanned/typed code, or `generate` for an internal one. */
export const addBarcodeRequestSchema = z.union([
    z.object({ code: barcodeCodeSchema }).strict(),
    z.object({ generate: z.literal(true) }).strict(),
]);
// Endpoints --------------------------------------------------------------------------------------------------
const catalog = { tag: "catalog" };
const view = { permission: "catalog.view" };
const manage = { permission: "catalog.manage" };
export const listProducts = defineEndpoint({
    ...catalog,
    name: "listProducts",
    method: "GET",
    path: "/v1/products",
    access: view,
    summary: "Products, newest first, a page at a time",
    query: productListQuerySchema,
    status: 200,
    response: productListSchema,
});
export const createProduct = defineEndpoint({
    ...catalog,
    name: "createProduct",
    method: "POST",
    path: "/v1/products",
    access: manage,
    summary: "Add a product with its options and variants",
    body: createProductRequestSchema,
    status: 201,
    response: productSchema,
    errors: [
        "SKU_TAKEN",
        "BARCODE_TAKEN",
        "PRICE_ABOVE_MRP",
        "TOO_MANY_VARIANTS",
        "FILE_REJECTED",
        "PLAN_LIMIT_REACHED",
    ],
    entitlement: "products.max",
});
export const getProduct = defineEndpoint({
    ...catalog,
    name: "getProduct",
    method: "GET",
    path: "/v1/products/{id}",
    access: view,
    summary: "One product with its options and variants",
    params: idParamsSchema,
    status: 200,
    response: productSchema,
});
export const updateProduct = defineEndpoint({
    ...catalog,
    name: "updateProduct",
    method: "PATCH",
    path: "/v1/products/{id}",
    access: manage,
    summary: "Change a product's details or options (optimistic version)",
    params: idParamsSchema,
    body: updateProductRequestSchema,
    status: 200,
    response: productSchema,
    errors: ["CONFLICT", "SKU_TAKEN", "BARCODE_TAKEN", "PRICE_ABOVE_MRP", "TOO_MANY_VARIANTS", "FILE_REJECTED"],
});
const productAction = (action, summary) => defineEndpoint({
    ...catalog,
    name: `${action}Product`,
    method: "POST",
    path: `/v1/products/{id}/${action}`,
    access: manage,
    summary,
    params: idParamsSchema,
    status: 200,
    response: productSchema,
    // Bringing a product back counts against the plan like a new one (decision 10).
    ...(action === "unarchive"
        ? { errors: ["PLAN_LIMIT_REACHED"], entitlement: "products.max" }
        : {}),
});
export const archiveProduct = productAction("archive", "Archive a product (hidden from POS and search, kept in reports)");
export const unarchiveProduct = productAction("unarchive", "Bring an archived product back");
export const updateVariant = defineEndpoint({
    ...catalog,
    name: "updateVariant",
    method: "PATCH",
    path: "/v1/variants/{id}",
    access: manage,
    summary: "Change a variant's SKU or image (optimistic version)",
    params: idParamsSchema,
    body: updateVariantRequestSchema,
    status: 200,
    response: variantSchema,
    errors: ["CONFLICT", "SKU_TAKEN"],
});
const variantAction = (action, summary) => defineEndpoint({
    ...catalog,
    name: `${action}Variant`,
    method: "POST",
    path: `/v1/variants/{id}/${action}`,
    access: manage,
    summary,
    params: idParamsSchema,
    status: 200,
    response: variantSchema,
    errors: ["BUSINESS_RULE_VIOLATION"],
});
export const archiveVariant = variantAction("archive", "Archive one variant (a product keeps at least one active)");
export const unarchiveVariant = variantAction("unarchive", "Bring an archived variant back");
export const addBarcode = defineEndpoint({
    ...catalog,
    name: "addBarcode",
    method: "POST",
    path: "/v1/variants/{id}/barcodes",
    access: manage,
    summary: "Add a barcode to a variant, or generate an internal one",
    params: idParamsSchema,
    body: addBarcodeRequestSchema,
    status: 201,
    response: barcodeSchema,
    errors: ["BARCODE_TAKEN"],
});
export const deleteBarcode = defineEndpoint({
    ...catalog,
    name: "deleteBarcode",
    method: "DELETE",
    path: "/v1/barcodes/{id}",
    access: manage,
    summary: "Remove a barcode from its variant",
    params: idParamsSchema,
    status: 204,
});
//# sourceMappingURL=catalog-products.js.map